import { useMemo } from 'react';
import {
  type ReceivedChatMessage,
  TextStreamData,
  useChat,
  useRoomContext,
  useTranscriptions,
} from '@livekit/components-react';
import { transcriptionToChatMessage } from '../utils/utils';

export default function useChatAndTranscription(chatOpen: boolean) {
  const transcriptions: TextStreamData[] = useTranscriptions();
  const chat = useChat();
  const room = useRoomContext();

  const mergedMessages = useMemo(() => {
    if (!chatOpen) {
      return [];
    }

    // 🔥 Convert transcriptions into chat messages
    const transcriptionMessages = transcriptions.map((t) => {
      const formatted = transcriptionToChatMessage(t, room);
      return formatted;
    });

    // 🔥 Combine everything
    const combined: Array<ReceivedChatMessage> = [
      ...transcriptionMessages,
      ...chat.chatMessages,
    ];
    // 🔥 Dedupe
    const map = new Map<string, ReceivedChatMessage>();

    for (const msg of combined) {
      const fallbackKey = `${msg.from?.identity ?? 'unknown'}:${String(msg.message).trim()}`;
      const key = msg.id ?? fallbackKey;

      const existing = map.get(key);

      if (!existing) {
        map.set(key, msg);
        continue;
      }

      // 🔥 If duplicates appear
      console.warn('%c[HOOK] DUPLICATE DETECTED:', 'color:#f60;', { existing, msg });

      const existingTs = existing.timestamp ?? 0;
      const msgTs = msg.timestamp ?? 0;

      if (msgTs > existingTs) {
        map.set(key, msg);
        continue;
      }

      if ((msg.message?.length ?? 0) > (existing.message?.length ?? 0)) {
        map.set(key, msg);
        continue;
      }
    }
    const deduped = Array.from(map.values());
    const sorted = deduped.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
    return sorted;
  }, [transcriptions, chat.chatMessages, room, chatOpen]);

  return { messages: mergedMessages, send: chat.send };
}
